import axios from "axios";
import { APP_BASE_URL } from "../Consts";
import { getPersistedAccessToken, clearPersistedSession } from "../sessionAccess";

export const apiClient = axios.create({
  baseURL: APP_BASE_URL,
});

/** Backend returns 200 JSON with success:false for many auth errors; normalize message. */
const authFailureMessage = (payload) => {
  const err = payload?.error ?? payload?.data?.error;
  const raw = err?.message ?? err?.description ?? "";
  return typeof raw === "string" ? raw.trim() : "";
};

const shouldForceLogoutMessage = (msg) => {
  const m = msg.toLowerCase();
  if (!m) return false;
  if (m === "invalid signature" || m === "jwt expired") return true;
  if (m.includes("jwt expired") || m.includes("invalid token")) return true;
  if (m.includes("invalid signature")) return true;
  // New JWTs include a `role` claim; old tokens fail verification with JsonWebTokenError text
  if (m.includes("jwt malformed") || m.includes("invalid jwt")) return true;
  return false;
};

export const normalizeBearer = (token) => {
  if (!token || typeof token !== "string") return token;
  const t = token.trim();
  if (/^bearer\s+/i.test(t)) return t;
  return `Bearer ${t}`;
};

/** Axios 1.x uses AxiosHeaders; plain property access can miss per-request Authorization. */
const getExplicitAuthHeader = (config) => {
  const headers = config?.headers;
  if (!headers) return null;
  if (typeof headers.get === "function") {
    return headers.get("Authorization") || headers.get("authorization") || null;
  }
  return headers.Authorization ?? headers.authorization ?? null;
};

const isTwoFactorAuthRequest = (config) =>
  Boolean(config.skipSessionAuth) ||
  /\/api\/admin\/2fa\//.test(String(config.url || ""));

apiClient.interceptors.request.use(
  (config) => {
    if (isTwoFactorAuthRequest(config)) {
      config.headers["x-timezone"] =
        Intl.DateTimeFormat().resolvedOptions().timeZone;
      return config;
    }

    const accessToken = getPersistedAccessToken();
    const hasAuthHeader = Boolean(getExplicitAuthHeader(config));
    if (accessToken && !hasAuthHeader) {
      config.headers[`Authorization`] = normalizeBearer(accessToken);
    }

    // Add user's device timezone
    config.headers['x-timezone'] = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

apiClient.interceptors.response.use(
  (response) => {
    const msg = authFailureMessage(response?.data);
    if (shouldForceLogoutMessage(msg)) {
      clearPersistedSession();
    }
    return response;
  },
  (error) => {
    const msg = authFailureMessage(error?.response?.data);
    if (shouldForceLogoutMessage(msg)) {
      clearPersistedSession();
    }
    return Promise.reject(error);
  }
);
